'use client';

import { useEffect, useRef, useState, type FormEvent } from 'react';

import { URLS } from '@/app/config/env';
import { usePostalCodeValidation } from '@/app/hooks/usePostalCodeValidation';
import { CONTACT_PANELS, checkContactField, formatPhoneInput, type ContactField as TextField } from './contact-validation';
import type { Answer, Option } from './flow';

/** The question bubble every input is labelled by. */
export const QUESTION_ID = 'tpc-q';

export const Arrow = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="M4 10h11M10.5 5.5 15 10l-4.5 4.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Check = () => (
  <svg className="tpc-check" viewBox="0 0 20 20" aria-hidden="true">
    <path d="M5 10.5l3.2 3.2L15 6.8" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const KEYS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/** Marks a capped, scrolling answer list `is-more` while there is more below (for the fade at its bottom edge). */
const useMoreBelow = () => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const list = ref.current;
    if (!list) return;
    // Measured from the last option's layout box (offsetTop ignores transforms), so an option still sliding in
    // during its entrance animation doesn't count as "more below" and fade the list out.
    const check = () => {
      const last = list.lastElementChild as HTMLElement | null;
      const bottom = last ? last.offsetTop + last.offsetHeight : 0;
      list.classList.toggle('is-more', bottom - list.scrollTop - list.clientHeight > 4);
    };
    check();
    list.addEventListener('scroll', check, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(check);
    observer?.observe(list);
    return () => {
      list.removeEventListener('scroll', check);
      observer?.disconnect();
    };
  }, []);
  return ref;
};

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Typeform-style key shortcuts: on the page in view, A, B, C… pick the options (not while typing). */
const useLetterKeys = (active: boolean, count: number, onKey: (index: number) => void) => {
  const handler = useRef(onKey);
  useEffect(() => {
    handler.current = onKey;
  });
  useEffect(() => {
    if (!active) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
      const index = KEYS.indexOf(event.key.toUpperCase());
      if (event.key.length !== 1 || index < 0 || index >= count) return;
      event.preventDefault();
      handler.current(index);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [active, count]);
};

/**
 * One tap answers: a lettered list (A, B, C, like a typeform). The pick flashes, then the form moves on.
 * `selected` shows an earlier answer when the visitor pages back to this question.
 */
export function ChoiceInput({
  options,
  onPick,
  selected,
  active = true,
  labelId = QUESTION_ID,
}: {
  options: ReadonlyArray<Option>;
  onPick: (value: string) => void;
  selected?: string;
  active?: boolean;
  labelId?: string;
}) {
  const [chosen, setChosen] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);

  const handlePick = (value: string) => {
    if (chosen) return;
    setChosen(value);
    timer.current = setTimeout(
      () => {
        setChosen(null);
        onPick(value);
      },
      reducedMotion() ? 0 : 420
    );
  };

  useLetterKeys(active, Math.min(options.length, KEYS.length), (index) => handlePick(options[index].value));

  const listRef = useMoreBelow();
  const on = chosen ?? selected;
  return (
    <div className="tpc-chips tpc-list" ref={listRef} role="group" aria-labelledby={labelId}>
      {options.map((option, i) => (
        <button
          key={option.value}
          className={chosen === option.value ? 'tpc-chip is-chosen' : 'tpc-chip'}
          type="button"
          aria-pressed={on === option.value}
          aria-keyshortcuts={KEYS[i]}
          onClick={() => handlePick(option.value)}
        >
          <span className="tpc-key" aria-hidden="true">
            {KEYS[i]}
          </span>
          <span className="tpc-chip-text">{option.label}</span>
          <Check />
        </button>
      ))}
    </div>
  );
}

/** US ZIP code, checked with the same validation as the standard form (usePostalCodeValidation). */
export function ZipInput({ onDone, initial = '', panelId }: { onDone: (zip: string) => void; initial?: string; panelId?: string }) {
  const { validate, formatInput } = usePostalCodeValidation('US');
  const [value, setValue] = useState(initial);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (checking) return;
    trackContinue(panelId);
    setChecking(true);
    const problem = await validate(value);
    setChecking(false);
    if (problem) {
      setError(/^\d*$/.test(value) ? 'Enter a five-digit ZIP code.' : problem);
      inputRef.current?.focus();
      return;
    }
    onDone(value);
  };

  return (
    <form className="tpc-form" noValidate onSubmit={handleSubmit}>
      <label className="sr-only" htmlFor="tpc-zip">
        ZIP code
      </label>
      <div className="tpc-inline">
        <input
          id="tpc-zip"
          ref={inputRef}
          className="tpc-input tpc-input-short"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={5}
          autoComplete="postal-code"
          enterKeyHint="next"
          placeholder="For example, 60614"
          value={value}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? 'tpc-zip-error' : undefined}
          onChange={(event) => {
            setError('');
            setValue(formatInput(event.target.value));
          }}
        />
        <button className="tpc-go" type="submit" disabled={value.length < 5 || checking}>
          {checking ? 'Checking…' : 'OK'} <Arrow />
        </button>
      </div>
      {error ? (
        <p className="tpc-error" id="tpc-zip-error" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
}

const MAX_INPUT_LENGTH = 256;

/** The standard form's Continue click, fired before its checks like the panels' (TextPanel.tsx, ZipCodePanel.tsx). */
const trackContinue = (panelId?: string) => {
  if (!panelId) return;
  void import('@/app/utils/web-analytics')
    .then(({ trackContinueButtonClick }) => trackContinueButtonClick(panelId))
    .catch(() => {});
};

/** The lead form's "field invalid" analytics event, with its labels (TextPanel.tsx). */
const trackInvalid = (field: TextField) => {
  const panel = CONTACT_PANELS[field];
  void import('@/app/utils/web-analytics')
    .then(({ trackFormFieldInvalid }) => trackFormFieldInvalid(panel.id, field === 'name' ? panel.id.replace(/-/g, '_') : field === 'phone' ? 'phone_number' : 'email'))
    .catch(() => {});
};

const TEXT_FIELDS: Record<'name' | 'email', { label: string; type: string; autoComplete: string; placeholder: string; inputMode?: 'email' }> = {
  name: { label: 'Full name', type: 'text', autoComplete: 'name', placeholder: 'First and last name' },
  email: { label: 'Email', type: 'email', autoComplete: 'email', placeholder: 'you@example.com', inputMode: 'email' },
};

/**
 * One contact field on its own page (the lead form's full-name and email panels), checked with the lead
 * form's rules (contact-validation.ts). Email may be left blank, as on the lead form.
 */
export function TextStepInput({ field, initial = '', onDone, panelId }: { field: 'name' | 'email'; initial?: string; onDone: (value: string) => void; panelId?: string }) {
  const spec = TEXT_FIELDS[field];
  const [value, setValue] = useState(initial);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const id = `tpc-${field}`;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (checking) return;
    trackContinue(panelId);
    setChecking(true);
    const result = await checkContactField(field, value);
    setChecking(false);
    if (result.error) {
      setError(result.error);
      trackInvalid(field);
      inputRef.current?.focus();
      return;
    }
    onDone(result.value);
  };

  return (
    <form className="tpc-form tpc-step-field" noValidate onSubmit={handleSubmit}>
      <label className="sr-only" htmlFor={id}>
        {spec.label}
      </label>
      <input
        id={id}
        ref={inputRef}
        className="tpc-input"
        type={spec.type}
        inputMode={spec.inputMode}
        autoComplete={spec.autoComplete}
        autoCapitalize={field === 'email' ? 'none' : 'words'}
        autoCorrect="off"
        spellCheck={false}
        enterKeyHint="next"
        maxLength={MAX_INPUT_LENGTH}
        placeholder={spec.placeholder}
        value={value}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(event) => {
          setError('');
          setValue(event.target.value);
        }}
      />
      {error ? (
        <p className="tpc-error" id={`${id}-error`} role="alert">
          {error}
        </p>
      ) : null}
      <div className="tpc-actions">
        <button className="tpc-go" type="submit" disabled={checking}>
          OK <Arrow />
        </button>
      </div>
    </form>
  );
}

/**
 * The last step (the lead form's phone panel): the phone number and its Terms of Use consent, then Submit.
 * `onSubmit` sends the lead with the phone's digits and returns an error to show, or null.
 */
export function PhoneSubmitForm({ onSubmit, labelId = QUESTION_ID, panelId }: { onSubmit: (phone: string) => Promise<string | null>; labelId?: string; panelId?: string }) {
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [consentError, setConsentError] = useState('');
  const [sendError, setSendError] = useState('');
  const [sending, setSending] = useState(false);
  const lock = useRef(false);
  const phoneRef = useRef<HTMLInputElement>(null);
  const consentRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (lock.current) return;
    lock.current = true;
    trackContinue(panelId);
    try {
      setSendError('');
      const checked = await checkContactField('phone', phone);
      setPhoneError(checked.error ?? '');
      setConsentError(consent ? '' : 'You must agree to the terms to continue');
      if (checked.error) {
        trackInvalid('phone');
        phoneRef.current?.focus();
        return;
      }
      if (!consent) {
        consentRef.current?.focus();
        return;
      }
      setSending(true);
      const problem = await onSubmit(checked.value.replace(/\D/g, ''));
      if (problem) {
        setSending(false);
        setSendError(problem);
      }
    } finally {
      lock.current = false;
    }
  };

  return (
    <form className="tpc-contact" noValidate onSubmit={handleSubmit} aria-labelledby={labelId}>
      <div className="tpc-field">
        <label className="sr-only" htmlFor="tpc-phone">
          Phone
        </label>
        <input
          id="tpc-phone"
          ref={phoneRef}
          className="tpc-input"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          enterKeyHint="done"
          maxLength={14}
          placeholder="(XXX) XXX-XXXX"
          value={phone}
          disabled={sending}
          aria-invalid={Boolean(phoneError)}
          aria-describedby={phoneError ? 'tpc-phone-error' : undefined}
          onChange={(event) => {
            setPhoneError('');
            setPhone(formatPhoneInput(event.target.value));
          }}
        />
        {phoneError ? (
          <p className="tpc-error" id="tpc-phone-error">
            {phoneError}
          </p>
        ) : null}
      </div>
      <div className="tpc-consent">
        <input
          id="tpc-consent"
          ref={consentRef}
          type="checkbox"
          checked={consent}
          disabled={sending}
          aria-invalid={Boolean(consentError)}
          aria-describedby={consentError ? 'tpc-consent-error' : undefined}
          onChange={(event) => {
            if (panelId) {
              void import('@/app/utils/web-analytics')
                .then(({ trackCheckboxClick }) => trackCheckboxClick(panelId, 'I agree with the Terms of Use'))
                .catch(() => {});
            }
            setConsentError('');
            setConsent(event.target.checked);
          }}
        />
        <label htmlFor="tpc-consent">
          I agree with the{' '}
          <a href={URLS.TERMS_OF_USE} target="_blank" rel="noopener noreferrer">
            Terms of Use
          </a>
        </label>
      </div>
      {consentError ? (
        <p className="tpc-error" id="tpc-consent-error">
          {consentError}
        </p>
      ) : null}
      <button className="tpc-submit" type="submit" disabled={sending}>
        {sending ? 'Sending…' : 'Submit'}
        {sending ? null : <Arrow />}
      </button>
      {sendError ? (
        <p className="tpc-error tpc-send-error" role="alert">
          {sendError}
        </p>
      ) : null}
    </form>
  );
}

const norm = (text: string): string => text.toLowerCase().replace(/[^a-z0-9+ ]/g, ' ').replace(/\s+/g, ' ').trim();

/** Longest typed text that can still be an answer ("my kid", "both"), not a sentence. */
const MAX_ANSWER_WORDS = 3;

/**
 * The option a typed answer names: the whole label, or a short part of exactly one label ("child",
 * "both"). A question, or a sentence that only mentions a label ("I'm not sure how this works"),
 * names none, so it goes to the chat instead of answering the form for the visitor.
 */
export const matchOptions = (text: string, options: ReadonlyArray<Option>): Option[] => {
  if (text.includes('?')) return [];
  const typed = norm(text);
  if (!typed) return [];
  const exact = options.filter((option) => norm(option.label) === typed);
  if (exact.length) return exact;
  if (typed.length < 3 || typed.split(' ').length > MAX_ANSWER_WORDS) return [];
  const partial = options.filter((option) => norm(option.label).includes(typed));
  return partial.length === 1 ? partial : [];
};

/**
 * The always-there message box at the bottom of the form, the same big box as the test prep home page
 * chat. A typed answer that names an option on the question in view counts as that pick; anything
 * else is a question (`onAsk`), answered on its own page.
 */
export function ComposerBox({
  options,
  disabled,
  busy = false,
  onAnswer,
  onAsk,
}: {
  options: ReadonlyArray<Option>;
  disabled: boolean;
  /** Luna is still answering an earlier question. */
  busy?: boolean;
  /** A typed message that names an option: the option's value, and what was typed. */
  onAnswer: (value: Answer, typed: string) => void;
  onAsk: (text: string) => void;
}) {
  const [text, setText] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (disabled || busy || !text.trim()) return;
    const found = options.length ? matchOptions(text, options) : [];
    setText('');
    if (found.length) onAnswer(found[0].value, text.trim());
    else onAsk(text.trim());
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    formRef.current?.requestSubmit();
  };

  return (
    <div className="hero-chat tpc-box">
      <form className="hc-form" ref={formRef} onSubmit={handleSubmit} noValidate>
        <label className="sr-only" htmlFor="tpc-type-input">
          Ask a question or type your answer
        </label>
        <div className="hc-field">
          <textarea
            id="tpc-type-input"
            rows={2}
            maxLength={500}
            enterKeyHint="send"
            placeholder={disabled ? 'One moment…' : 'Ask us anything, or type your answer'}
            value={text}
            disabled={disabled}
            aria-describedby="tpc-type-tip"
            onChange={(event) => setText(event.target.value)}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div className="hc-foot">
          <span className="hc-tip" id="tpc-type-tip" role={busy ? 'status' : undefined}>
            {busy ? 'Answering your question…' : 'Press Enter to send.'}
          </span>
          <button className="hc-send" type="submit" disabled={disabled || busy || !text.trim()}>
            Send <Arrow />
          </button>
        </div>
      </form>
    </div>
  );
}
